/**
 * Validierung, die Frontend und Control Plane teilen. Das Frontend nutzt sie fuer
 * sofortiges Feedback, die Control Plane als verbindliche Pruefung — die
 * Client-Pruefung ist Komfort, nicht Sicherheit.
 */
const OK = { ok: true };
/**
 * Teams-Join-Links haben zwei gaengige Formen:
 *   https://teams.microsoft.com/l/meetup-join/19%3ameeting_...%40thread.v2/0?context=...
 *   https://teams.microsoft.com/meet/<id>?p=<passcode>            (neuere Kurzform)
 * Zusaetzlich existieren Tenant-eigene Hosts wie teams.microsoft.us (GCC).
 */
const TEAMS_HOSTS = [
    'teams.microsoft.com',
    'teams.microsoft.us',
    'dod.teams.microsoft.us',
    'teams.live.com',
];
export function validateTeamsMeetingUrl(raw) {
    const value = raw.trim();
    if (value.length === 0)
        return { ok: false, error: 'Meeting-Link fehlt.' };
    let url;
    try {
        url = new URL(value);
    }
    catch {
        return { ok: false, error: 'Kein gueltiger Link.' };
    }
    if (url.protocol !== 'https:') {
        return { ok: false, error: 'Meeting-Link muss https:// sein.' };
    }
    const host = url.hostname.toLowerCase();
    if (!TEAMS_HOSTS.includes(host)) {
        return {
            ok: false,
            error: `Unerwarteter Host "${host}". Erwartet wird ein Teams-Link (${TEAMS_HOSTS.join(', ')}).`,
        };
    }
    const path = decodeURIComponent(url.pathname);
    const isMeetupJoin = path.includes('/l/meetup-join/') && path.includes('@thread.');
    const isShortMeet = /^\/(meet|v2)\//.test(url.pathname);
    if (!isMeetupJoin && !isShortMeet) {
        return {
            ok: false,
            error: 'Der Link sieht nicht wie ein Teams-Beitrittslink aus (erwartet /l/meetup-join/... oder /meet/...).',
        };
    }
    return OK;
}
/**
 * Prueft eine AVA-URL.
 *
 * Ist die Allowlist leer, wird der Host NICHT eingeschraenkt — geprueft bleiben
 * Linkform und https. Ist sie gefuellt, muss der Host darin stehen; ein Eintrag
 * mit fuehrendem Punkt (".ava.example.com") erlaubt auch Subdomains.
 */
export function validateAvatarUrl(raw, allowlist, options = {}) {
    const value = raw.trim();
    if (value.length === 0)
        return { ok: false, error: 'AVA-URL fehlt.' };
    let url;
    try {
        url = new URL(value);
    }
    catch {
        return { ok: false, error: 'Kein gueltiger Link.' };
    }
    const insecureOk = options.allowInsecure === true;
    if (url.protocol !== 'https:' && !(insecureOk && url.protocol === 'http:')) {
        return { ok: false, error: 'AVA-URL muss https:// sein.' };
    }
    /*
     * Leere Liste = keine Host-Einschraenkung.
     *
     * Umgestellt am 10.09.2026 auf Wunsch: die AVA-Seiten liegen nicht mehr nur
     * unter einem Host. Vorher galt leer als "nichts erlaubt", was als
     * Sicherheitsnetz gedacht war, hier aber nur den Betrieb blockiert haette.
     *
     * Was weiterhin geprueft wird, bleibt die eigentliche Huerde: es muss ein
     * gueltiger Link sein und https (ausser im ausdruecklichen Testmodus). Damit
     * sind javascript:, data: und file: weiter ausgeschlossen.
     */
    if (allowlist.length === 0)
        return OK;
    if (!isHostAllowed(url.hostname, allowlist)) {
        return {
            ok: false,
            error: `Host "${url.hostname}" ist nicht freigegeben. Erlaubt: ${allowlist.join(', ')}.`,
        };
    }
    return OK;
}
export function isHostAllowed(hostname, allowlist) {
    const host = hostname.toLowerCase().replace(/\.$/, '');
    return allowlist.some((entryRaw) => {
        const entry = entryRaw.trim().toLowerCase();
        if (entry.length === 0)
            return false;
        if (entry.startsWith('.')) {
            const base = entry.slice(1);
            return host === base || host.endsWith(entry);
        }
        return host === entry;
    });
}
/** Laengengrenzen fuer den eingegebenen Anzeigenamen (ohne die KI-Kennzeichnung). */
export const DISPLAY_NAME_MIN_LENGTH = 2;
export const DISPLAY_NAME_MAX_LENGTH = 48;
/**
 * Steuer- und Formatzeichen im Anzeigenamen. Das ist nicht Kosmetik: U+202E
 * (RIGHT-TO-LEFT OVERRIDE) und Verwandte koennen die Darstellung so umdrehen,
 * dass die angehaengte KI-Kennzeichnung optisch woanders landet oder unsichtbar
 * wird. Da die Kennzeichnung eine Zusage an die Meeting-Teilnehmer ist, wird
 * hier abgelehnt statt still bereinigt.
 */
const CONTROL_OR_FORMAT = /[\p{Cc}\p{Cf}]/u;
/**
 * Der Anzeigename wird vom Mitarbeiter eingegeben und ist verpflichtend — sonst
 * stuende im Meeting nur "(KI-Avatar)" ohne Bezug zu einem Avatar.
 * Die KI-Kennzeichnung haengt `ensureAiMarker` serverseitig an; sie zaehlt nicht
 * in diese Laengengrenze.
 */
export function validateDisplayName(raw) {
    const value = raw.trim().replace(/\s+/g, ' ');
    if (value.length === 0) {
        return { ok: false, error: 'Bitte einen Anzeigenamen fuer den Avatar angeben.' };
    }
    if (value.length < DISPLAY_NAME_MIN_LENGTH) {
        return { ok: false, error: `Anzeigename ist zu kurz (min. ${DISPLAY_NAME_MIN_LENGTH} Zeichen).` };
    }
    if (value.length > DISPLAY_NAME_MAX_LENGTH) {
        return { ok: false, error: `Anzeigename ist zu lang (max. ${DISPLAY_NAME_MAX_LENGTH} Zeichen).` };
    }
    if (CONTROL_OR_FORMAT.test(value)) {
        return { ok: false, error: 'Anzeigename enthaelt unerlaubte Steuerzeichen.' };
    }
    return OK;
}
export function validateStartedBy(raw) {
    const value = raw.trim();
    if (value.length < 2)
        return { ok: false, error: 'Bitte einen Namen angeben (min. 2 Zeichen).' };
    if (value.length > 64)
        return { ok: false, error: 'Name ist zu lang (max. 64 Zeichen).' };
    return OK;
}
export function parseTeamsMeetingLocator(raw) {
    let url;
    try {
        url = new URL(raw.trim());
    }
    catch {
        return null;
    }
    const short = /^\/(?:meet|v2)\/([^/?]+)/.exec(url.pathname);
    if (short?.[1] !== undefined) {
        const passcode = url.searchParams.get('p');
        return {
            kind: 'id',
            meetingId: decodeURIComponent(short[1]),
            passcode: passcode === null || passcode.length === 0 ? undefined : passcode,
        };
    }
    if (decodeURIComponent(url.pathname).includes('/l/meetup-join/')) {
        return { kind: 'link', meetingLink: url.toString() };
    }
    return null;
}
/** Kuerzt einen Meeting-Link auf etwas, das man in einer Liste anzeigen kann. */
export function meetingLabelFromUrl(raw) {
    try {
        const url = new URL(raw);
        const path = decodeURIComponent(url.pathname);
        const threadMatch = /19[:%]3?a?([^@/]+)@thread\./i.exec(path);
        if (threadMatch?.[1]) {
            const id = threadMatch[1];
            return `Teams-Meeting …${id.slice(-8)}`;
        }
        const shortMatch = /^\/(?:meet|v2)\/([^/?]+)/.exec(url.pathname);
        if (shortMatch?.[1])
            return `Teams-Meeting ${shortMatch[1]}`;
        return `Teams-Meeting (${url.hostname})`;
    }
    catch {
        return 'Teams-Meeting';
    }
}
//# sourceMappingURL=validation.js.map